import { useRef, useState } from "react";
import { BellIcon } from "./Icons";
import PopoverAnchor, { useClampToViewport } from "./Popover";
import type { BroadcastNotification } from "./broadcastNotify";
import * as sounds from "./sounds";

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return sameDay ? time : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${time}`;
}

/** Bell icon + unread badge, next to the What's New button — opens a
 * dropdown of recent admin broadcasts. Opening it counts as reading
 * everything currently in the list (same "seen" model as What's New). */
export default function NotificationBell({
  history,
  unreadCount,
  onMarkAllRead,
}: {
  history: BroadcastNotification[];
  unreadCount: number;
  onMarkAllRead: () => void;
}) {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  useClampToViewport(popRef, [open, history]);

  return (
    <PopoverAnchor active={open} onDismiss={() => setOpen(false)}>
      <button
        className={`theme-toggle notif-btn ${unreadCount > 0 ? "has-new" : ""}`}
        title="Notifications"
        aria-label="Notifications"
        onClick={() => {
          sounds.playOnce(sounds.SOUND.click);
          setOpen((v) => !v);
          if (!open) onMarkAllRead();
        }}
      >
        <BellIcon size={17} />
        {unreadCount > 0 && (
          <span className="notif-btn__count">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>
      {open && (
        <div className="notif-pop" ref={popRef}>
          <div className="notif-pop__head">Notifications</div>
          {history.length === 0 ? (
            <div className="sect-empty">Nothing yet</div>
          ) : (
            [...history].reverse().map((n) => (
              <div className="notif-pop__row" key={n.id}>
                <div className="notif-pop__msg">{n.message}</div>
                <div className="notif-pop__time">{formatWhen(n.created_at)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </PopoverAnchor>
  );
}
